<template>
    <Toast />
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-3">
                    <div>
                        <h5 class="m-0">Lista de compras</h5>
                        <small class="text-color-secondary">Lo que quedó debajo de su nivel par, por proveedor. Ajuste las cantidades antes de pedir.</small>
                    </div>
                    <div class="flex flex-wrap gap-2 mt-2 md:mt-0">
                        <Button label="Ajustes" icon="pi pi-arrow-left" class="p-button-text" @click="$router.push('/dashboard/inventario/ajustes')" />
                        <Button icon="pi pi-refresh" class="p-button-outlined" v-tooltip.top="'Actualizar'" :loading="loading" @click="load" />
                        <Button label="Copiar para WhatsApp" icon="pi pi-copy" class="p-button-outlined" :disabled="!text" @click="copy" />
                        <Button label="Imprimir" icon="pi pi-print" class="p-button-outlined" :disabled="!text" @click="print" />
                    </div>
                </div>

                <Message v-if="data && data.staleCount > 0" severity="warn" :closable="false">
                    {{ data.staleCount }} insumo(s) de conteo periódico <i class="pi pi-exclamation-triangle"></i> no se contaron
                    en las últimas 24 horas. Su stock solo baja al contar: cuéntelos antes de pedir.
                    <Button label="Contar ahora" class="p-button-link p-0 ml-1" @click="$router.push('/dashboard/inventario/conteo')" />
                </Message>

                <div v-if="loading && !data" class="text-center p-4"><i class="pi pi-spin pi-spinner" style="font-size: 1.5rem"></i></div>

                <div v-else-if="data && data.itemCount === 0" class="text-center p-5">
                    <i class="pi pi-check-circle text-green-500" style="font-size: 2.5rem"></i>
                    <h5 class="mt-3 mb-1">Nada por reponer</h5>
                    <small class="text-color-secondary">Solo aparecen los productos con nivel par: se define en la ficha de cada producto, en la sección Inventario.</small>
                </div>

                <template v-else-if="data">
                    <div v-for="provider in data.providers" :key="provider.providerId || 'none'" class="mb-4">
                        <div class="flex justify-content-between align-items-baseline mb-2">
                            <h6 class="m-0">{{ provider.providerName }}</h6>
                            <span v-if="providerCost(provider) !== null" class="text-sm text-color-secondary">Estimado {{ formatMoney(providerCost(provider)) }}</span>
                        </div>
                        <DataTable :value="provider.items" dataKey="productId" responsiveLayout="scroll" class="p-datatable-sm">
                            <Column header="Producto">
                                <template #body="{ data: item }">
                                    {{ item.productName }}
                                    <Tag v-if="item.periodic" value="Conteo" severity="info" class="ml-1" />
                                    <i v-if="item.stale" class="pi pi-exclamation-triangle text-orange-500 ml-1"
                                       v-tooltip.top="item.lastCountedAt ? 'Último conteo: ' + formatDate(item.lastCountedAt) : 'Nunca se contó'"></i>
                                </template>
                            </Column>
                            <Column header="Stock"><template #body="{ data: item }">{{ formatQty(item.stock) }}</template></Column>
                            <Column header="Nivel par"><template #body="{ data: item }">{{ formatQty(item.parLevel) }}</template></Column>
                            <Column header="Pedir" style="width: 9rem">
                                <template #body="{ data: item }">
                                    <InputNumber v-model="quantities[item.productId]" mode="decimal" :min="0"
                                                 :minFractionDigits="0" :maxFractionDigits="3" :useGrouping="false"
                                                 locale="en-US" inputClass="w-full qty-input" />
                                </template>
                            </Column>
                            <Column header="Costo est.">
                                <template #body="{ data: item }">{{ itemCost(item) === null ? '—' : formatMoney(itemCost(item)) }}</template>
                            </Column>
                        </DataTable>
                    </div>

                    <div class="flex justify-content-end font-bold">
                        Total estimado: {{ formatMoney(totalCost) }}
                    </div>
                    <small class="block text-right text-color-secondary">Con el precio de la última compra de cada producto.</small>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';
import { buildShoppingText, formatDate, formatMoney, formatQty } from './inventoryFormat';

export default {
    name: 'ShoppingList',
    data() {
        return {
            loading: false,
            data: null,
            // Lo que se va a pedir, por producto. Arranca en la sugerida.
            quantities: {},
        };
    },
    created() {
        if (!this.$can('inventoryManage')) {
            this.$router.replace('/dashboard');
            return;
        }
        this.load();
    },
    computed: {
        text() {
            return this.data ? buildShoppingText(this.data.providers, this.quantities) : '';
        },
        totalCost() {
            if (!this.data) return 0;
            return this.data.providers.reduce((sum, p) => sum + (this.providerCost(p) || 0), 0);
        },
    },
    methods: {
        formatDate, formatMoney, formatQty,
        async load() {
            this.loading = true;
            try {
                const r = await httpRequest.getShoppingList();
                if (r.status === 200 && r.data) {
                    this.data = r.data;
                    const quantities = {};
                    for (const provider of r.data.providers) {
                        for (const item of provider.items) quantities[item.productId] = Number(item.suggested);
                    }
                    this.quantities = quantities;
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo cargar la lista', life: 4000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.loading = false;
            }
        },
        // El costo estimado viene para la cantidad sugerida; si se edita, se escala.
        itemCost(item) {
            const suggested = Number(item.suggested);
            if (item.estimatedCost === null || item.estimatedCost === undefined || !suggested) return null;
            const quantity = Number(this.quantities[item.productId]) || 0;
            return Number(item.estimatedCost) / suggested * quantity;
        },
        providerCost(provider) {
            const costs = provider.items.map(this.itemCost).filter(c => c !== null);
            return costs.length ? costs.reduce((a, b) => a + b, 0) : null;
        },
        async copy() {
            try {
                await navigator.clipboard.writeText(this.text);
                this.$toast.add({ severity: 'success', summary: 'Copiado', detail: 'Pégalo en el chat del proveedor', life: 3000 });
            } catch (e) {
                this.$toast.add({ severity: 'error', summary: 'No se pudo copiar', detail: 'Use «Imprimir» en su lugar', life: 4000 });
            }
        },
        print() {
            const win = window.open('', '_blank');
            if (!win) return;
            const escape = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const body = escape(this.text).replace(/\*([^*\n]+)\*/g, '<b>$1</b>');
            win.document.write(
                '<!doctype html><html><head><meta charset="utf-8"><title>Lista de compras</title>'
                + '<style>body{font-family:system-ui,sans-serif;margin:2rem}pre{font:inherit;white-space:pre-wrap;line-height:1.6}</style>'
                + '</head><body><h2>Lista de compras</h2><pre>' + body + '</pre></body></html>');
            win.document.close();
            win.focus();
            win.print();
        },
    },
};
</script>

<style scoped>
:deep(.qty-input) {
    width: 7rem;
    text-align: right;
}
</style>
